import { useRef, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import './Reports.css';
import MetricCard from './Overview/MetricCard';
import ReportDateFilters from './ReportDateFilters';
import SearchSelectBox from './SearchSelectBox';
import { ClientConfigurationContext } from '../../context/ClientConfigurationContextProvider';

export default function ReportSection({
    id,
    title,
    pageTitle = false,
    cards,
    dateFilters,
    sweepsDateFilters,
    additionalFilters,
    applyFilters = () => { },
    applyDateFilters,
    appliedFilters,
    setAppliedFilters = () => { },
    selectedDateRange,
    setSelectedDateRange = () => { },
    filterOptions,
    dataStartDate,
    dataLastUpdatedDate,
    printable = false,
    onExport,
    exportDisabled = false,
    children
}) {
    const clientConfiguration = useContext(ClientConfigurationContext);
    const modalRef = useRef();
    function openModal() { modalRef.current.open = true; }
    function closeModal() { modalRef.current.open = false; }

    function handleRemoveFilter(filterId, value) {
        const filtersAfterRemove = {
            ...appliedFilters,
            [filterId]: appliedFilters[filterId].filter(item => item !== value)
        };
        if (filtersAfterRemove[filterId].length === 0) {
            delete filtersAfterRemove[filterId];
        }
        setAppliedFilters(filtersAfterRemove);
        applyFilters(filtersAfterRemove);
    }

    function normalizeFilters(filters) {
        return Object.fromEntries(
            Object.entries(filters ?? {}).filter(([key, value]) => {
                if (key === 'startDate' || key === 'endDate') {
                    return !!value;
                }
                return Array.isArray(value) ? value.length > 0 : !!value;
            })
        );
    }

    function handleApplyFilters(filters) {
        const normalizedFilters = normalizeFilters(filters);
        setAppliedFilters(normalizedFilters);
        applyFilters(normalizedFilters);
        closeModal();
    }

    function handleDateFilters(dateFilters) {
        const dateFilterHandler = applyDateFilters ?? applyFilters;

        if (Object.keys(dateFilters).length === 0) {
            const { startDate, endDate, ...otherFilters } = appliedFilters ?? {};
            const normalizedFilters = normalizeFilters(otherFilters);
            setAppliedFilters(normalizedFilters);
            dateFilterHandler(normalizedFilters);
            return;
        }

        const mergedFilters = normalizeFilters({ ...appliedFilters, ...dateFilters });
        setAppliedFilters(mergedFilters);
        dateFilterHandler(mergedFilters);
    }

    function FiltersModal() {
        const [filters, setFilters] = useState(appliedFilters);

        const handleSetFieldValue = (filterKey, values) => {
            setFilters(prevFilters => ({
                ...prevFilters,
                [filterKey]: values
            }));
        };

        function handleClearFilters() {
            setFilters({});
        }

        return (
            <gds-modal type="modal" ref={modalRef}>
                <span slot="header">Report Filters</span>
                <div className='report-filters-modal'>
                    {additionalFilters.map((filter) => (
                        <div className='filter-item' key={filter.value}>
                            <SearchSelectBox
                                id={`multiselect-${filter.value}`}
                                setFieldValue={(e, values) => handleSetFieldValue(filter.value, values)}
                                value={filters[filter.value] ?? []}
                                options={filterOptions?.[filter.optionsKey]?.filter(option => option !== null)}
                                label={filter.label}
                                multipleSelect={true}
                            />
                        </div>
                    ))}
                </div>
                <gds-button appearance="primary" slot="buttons" onClick={() => handleApplyFilters(filters)}>Apply</gds-button>
                <gds-button appearance="secondary" slot="buttons" onClick={handleClearFilters}>Clear</gds-button>
                <gds-button appearance="secondary" slot="buttons" onClick={closeModal}>Cancel</gds-button>
            </gds-modal>
        );
    }

    return (
        <div className="report-section">
            <div className={`title-container ${printable ? 'add-print-button' : ''}`}>
                {title && <div className={`title ${pageTitle ? 'page-title' : ''}`}>{title}</div>}
                {printable && clientConfiguration.enablePrintReportButton &&
                    <div className="title-actions">
                        {onExport &&
                            <gds-icon-button
                                onClick={onExport}
                                icon-type="download"
                                size="small"
                                className="export-button"
                                disabled={exportDisabled || undefined}
                            >
                                Export
                            </gds-icon-button>
                        }
                        <gds-icon-button onClick={window.print} icon-type="print" size="small" className="print-button" > Print </gds-icon-button>
                    </div>
                }
            </div>
            <div className="filters">
                {(dateFilters || sweepsDateFilters) && dataStartDate && dataLastUpdatedDate &&
                    <ReportDateFilters
                        applyFilters={handleDateFilters}
                        selectedFilter={selectedDateRange}
                        setSelectedFilter={setSelectedDateRange}
                        allInStartDate={dataStartDate}
                        allInEndDate={dataLastUpdatedDate}
                        customStartDate={selectedDateRange === "Custom" ? appliedFilters.startDate : null}
                        customEndDate={selectedDateRange === "Custom" ? appliedFilters.endDate : null}
                        sweeps={sweepsDateFilters}
                    />}
                {additionalFilters && appliedFilters &&
                    <div id="reports-pill-filters" className="pill-filter">
                        {Object.entries(appliedFilters)
                            .filter(([key]) => key !== "startDate" && key !== "endDate")
                            .flatMap(([key, values]) =>
                                values.map(value => (
                                    <button key={`${key}-${value}-pill`} className="btn btn--pill-filter btn--selected"
                                        type="button" onClick={() => handleRemoveFilter(key, value)}>
                                        {value} <gds-icon icon-type="close" size="xsmall" />
                                    </button>
                                ))
                            )}
                        <button id={`add-filters-pill`} type="button" className='btn btn--pill-filter single-choice'
                            onClick={openModal} >
                            <div className="filter-button">
                                Add Filters
                            </div>
                        </button>
                    </div>
                }
            </div>
            <div id={id || undefined} className="content">
                {cards &&
                    <div className='cards'>
                        {Object.entries(cards).map(([key, value]) => (
                            <MetricCard key={key} number={value} title={key} />
                        ))}
                    </div>
                }
                {children &&
                    <div className='charts'>
                        {children}
                    </div>
                }
            </div>
            {
                additionalFilters &&
                <FiltersModal />
            }
        </div >
    );
}

ReportSection.propTypes = {
    id: PropTypes.string,
    title: PropTypes.string,
    pageTitle: PropTypes.bool,
    cards: PropTypes.object,
    dateFilters: PropTypes.bool,
    sweepsDateFilters: PropTypes.bool,
    additionalFilters: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        optionsKey: PropTypes.string.isRequired
    })),
    applyFilters: PropTypes.func,
    applyDateFilters: PropTypes.func,
    appliedFilters: PropTypes.object,
    setAppliedFilters: PropTypes.func,
    selectedDateRange: PropTypes.string,
    setSelectedDateRange: PropTypes.func,
    filterOptions: PropTypes.object,
    dataStartDate: PropTypes.instanceOf(Date),
    dataLastUpdatedDate: PropTypes.instanceOf(Date),
    printable: PropTypes.bool,
    onExport: PropTypes.func,
    exportDisabled: PropTypes.bool,
    children: PropTypes.node
};